import { useState } from 'react';
import { Search, Activity, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export function BookingLookupForm({ onSubmit, isLoading }) {
  const [pnr, setPnr] = useState('');
  const [lastname, setLastname] = useState('');
  const [enableMonitoring, setEnableMonitoring] = useState(false);
  const [monitoringFrequency, setMonitoringFrequency] = useState('daily');
  const [monitoringUntil, setMonitoringUntil] = useState('');
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!pnr || pnr.length !== 6) {
      newErrors.pnr = 'PNR muss genau 6 Zeichen lang sein';
    } else if (!/^[A-Z0-9]+$/.test(pnr)) {
      newErrors.pnr = 'PNR muss alphanumerisch sein';
    }

    if (!lastname || lastname.length < 1) {
      newErrors.lastname = 'Nachname erforderlich';
    }

    if (enableMonitoring && !monitoringUntil) {
      newErrors.monitoringUntil = 'Enddatum für Überwachung erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    onSubmit({
      pnr,
      lastname: lastname.toUpperCase(),
      enableMonitoring,
      monitoringFrequency,
      monitoringUntil: enableMonitoring ? new Date(monitoringUntil).toISOString() : null,
    });
  };

  // Get default monitoring end date (30 days from now)
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* PNR and Lastname Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="pnr" className="text-slate-700">
            PNR / Buchungsreferenz
          </Label>
          <Input
            id="pnr"
            type="text"
            value={pnr}
            onChange={(e) => setPnr(e.target.value.toUpperCase().slice(0, 6))}
            placeholder="JNPEE5"
            maxLength={6}
            className={cn(
              'font-mono text-lg tracking-wider',
              errors.pnr && 'border-red-500 focus-visible:ring-red-500'
            )}
          />
          {errors.pnr && (
            <p className="text-sm text-red-500">{errors.pnr}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastname" className="text-slate-700">
            Nachname
          </Label>
          <Input
            id="lastname"
            type="text"
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            placeholder="Kardaslar"
            className={cn(
              errors.lastname && 'border-red-500 focus-visible:ring-red-500'
            )}
          />
          {errors.lastname && (
            <p className="text-sm text-red-500">{errors.lastname}</p>
          )}
        </div>
      </div>

      {/* Monitoring Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200">
        <div className="flex items-center gap-3">
          <Activity className="h-5 w-5 text-slate-500" />
          <div>
            <p className="font-medium text-slate-900">Überwachung aktivieren</p>
            <p className="text-sm text-slate-500">
              Buchungsänderungen automatisch prüfen
            </p>
          </div>
        </div>
        <Switch
          checked={enableMonitoring}
          onCheckedChange={setEnableMonitoring}
        />
      </div>

      {/* Monitoring Options */}
      {enableMonitoring && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg bg-highlight-hover border border-highlight">
          <div className="space-y-2">
            <Label htmlFor="frequency" className="text-slate-700">
              Prüfintervall
            </Label>
            <Select value={monitoringFrequency} onValueChange={setMonitoringFrequency}>
              <SelectTrigger id="frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Stündlich</SelectItem>
                <SelectItem value="daily">Täglich</SelectItem>
                <SelectItem value="weekly">Wöchentlich</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="until" className="text-slate-700">
              Überwachen bis
            </Label>
            <Input
              id="until"
              type="date"
              value={monitoringUntil}
              onChange={(e) => setMonitoringUntil(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className={cn(
                errors.monitoringUntil && 'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {errors.monitoringUntil && (
              <p className="text-sm text-red-500">{errors.monitoringUntil}</p>
            )}
            {!monitoringUntil && (
              <button
                type="button"
                onClick={() => setMonitoringUntil(getDefaultEndDate())}
                className="text-xs text-highlight-text hover:underline"
              >
                Auf 30 Tage ab heute setzen
              </button>
            )}
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isLoading}
        className="w-full h-12 bg-gradient-to-r from-action-from to-action-to hover:from-action-hover-from hover:to-action-hover-to text-action-text font-medium"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Buchung wird abgerufen...
          </>
        ) : (
          <>
            <Search className="h-5 w-5 mr-2" />
            Buchung abrufen
          </>
        )}
      </Button>
    </form>
  );
}
